import { useContext, useEffect, useRef } from 'react';
import { SegmentChatContext } from '@trinity/chat-components/components/BrandInsights/SegmentChat.Context';
import { useLocation } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import type { ChatSidePanelRouteAdapter } from '../side-panel-route-policy';

/**
 * Clears segment-scoped chat state (`segmentChatStatus`, `segmentIconUrl`) whenever the user
 * navigates away from the Segments scope (see `isSegmentsScopePath`: the individual
 * segment dashboard `/projects/:projectId/dashboard/:segmentId` — where segment chat state is
 * actually set — and the Segments list tab `/projects/:slug/segments`) to any other project
 * view — Project overview, Transcripts (`calls`), Documents (`files`), tags, etc.
 *
 * Segment state otherwise persists indefinitely across navigation (by design, so the chat session
 * survives tab switches), so without this it can leak a segment persona avatar into a
 * non-Segments conversation. Scoped to the "leaving Segments scope" transition only — navigating
 * within that scope (e.g. switching segments) intentionally preserves segment state, as does
 * navigating between other project tabs that never touched Segments.
 *
 * The segment link itself lives on the chat context (`metadata.segment`), which follows the
 * registered page context, so there is no filter tag to clear here.
 *
 * Must be called from inside `SegmentChatContainer` so the `SegmentChatContext` setters are
 * available, and under a `ChatRoutingProvider`.
 *
 * `isSegmentsScopePath` is app-specific route knowledge (the example paths above are the legacy
 * frontend's); pass a stable reference, as it is a dependency of the effect below.
 */
export function useSegmentContextResetOnProjectRoot({
  isSegmentsScopePath,
}: Pick<ChatSidePanelRouteAdapter, 'isSegmentsScopePath'>): void {
  const { segmentChatStatus, setSegmentChatStatus, setSegmentIconUrl } = useContext(SegmentChatContext);
  const { pathname } = useLocation();
  const prevPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    const prevPathname = prevPathnameRef.current;
    const isNavigation = prevPathname !== null && prevPathname !== pathname;
    prevPathnameRef.current = pathname;

    const wasOnSegmentsTab = prevPathname !== null && isSegmentsScopePath(prevPathname);
    const isLeavingSegmentsTab = wasOnSegmentsTab && !isSegmentsScopePath(pathname);

    if (!isNavigation || !isLeavingSegmentsTab || !segmentChatStatus) {
      return;
    }

    setSegmentChatStatus(false);
    setSegmentIconUrl(undefined);
  }, [pathname, segmentChatStatus, setSegmentChatStatus, setSegmentIconUrl, isSegmentsScopePath]);
}
